import { useMutation } from "@tanstack/vue-query";

import URLS from "@/autogen/urls";
import type { Newsletter } from "@/types/newsletter";

import type { BasicSubscriber } from "../lib";

interface UseResubscribeOptions {
  newsletter: Newsletter;
  onSuccess?: (subscriber: BasicSubscriber) => void;
  onError?: (error: string) => void;
}

export function useResubscribe({
  newsletter,
  onSuccess,
  onError,
}: UseResubscribeOptions) {
  return useMutation({
    async mutationFn(subscriberId: string) {
      const type = newsletter.should_disable_non_premium_subscriptions
        ? "unpaid"
        : "regular";

      const response = await fetch(
        URLS["api:retrieve_subscriber"](subscriberId),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type }),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.detail || "Failed to resubscribe");
      }

      const data = await response.json();
      const sub: BasicSubscriber = {
        id: data.subscriber.id,
        email_address: data.subscriber.email_address,
        type: data.subscriber.type,
        newsletter_id: newsletter.id,
      };
      return sub;
    },
    onSuccess(subscriber) {
      onSuccess?.(subscriber);
    },
    onError(error: Error) {
      onError?.(error.message);
    },
  });
}
